/**
 * The conversation stream — persisted history, the optimistic user turn, and the
 * live detached-run view, top to bottom. History and the live run share the
 * {@link StreamItemView} leaf, so tokens / tool events / media refs / directives
 * render identically whether replayed or tailing. Auto-scrolls to the newest
 * content unless the user has scrolled up — see {@link shouldFollow}, which is
 * the guarantee this comment used to assert and the code did not keep.
 */
import type { Locale } from '@4f/i18n';
import { Avatar, Button, ChatBubble, formatDateTime, useT } from '@4f/ui';
import { createEffect, createSignal, For, type JSX, Match, on, Show, Switch } from 'solid-js';
import type { CompanionView, MessageView } from '../../api/types.ts';
import type { ChatStore } from '../../stores/chat.ts';
import { messageItems, messageText } from './content.ts';
import { Paywall } from './Paywall.tsx';
import { paywallFromCode, paywallFromError } from './paywall-model.ts';
import { type StreamHandlers, StreamItemView } from './StreamItemView.tsx';
import { shouldFollow, withDaySeparators } from './thread-model.ts';

export interface ChatStreamProps {
  readonly store: ChatStore;
  readonly companion: CompanionView;
  /** Her resolved avatar URL (`avatarAssetId` → e2 URL), or `undefined` (initials fallback). */
  readonly avatarSrc?: string;
  readonly timeZone: string;
  readonly locale: Locale;
  /** Open the shared Artifacts lightbox at a clicked inline `gallery` directive. */
  readonly onOpenGallery?: StreamHandlers['onOpenGallery'];
  /** Open a modal surface from a `modal` directive (builder/settings/…). */
  readonly onOpenSurface?: StreamHandlers['onOpenSurface'];
  /** Resolve a `gallery` directive's asset ids to fetchable URLs. */
  readonly assetUrl?: StreamHandlers['assetUrl'];
}

export function ChatStream(props: ChatStreamProps): JSX.Element {
  const { t } = useT();
  const handlers: StreamHandlers = {
    submit: (id, value) => props.store.submit(id, value),
    // A `file` directive parks the run on `waiting_input`. Without an `onFile`
    // seam the renderer degrades to a hint with NO file input, so the run never
    // resumes and the conversation is unrecoverable without a reload. The store's
    // upload goes through the edge's moderated concept ingest (red line 2 — your
    // own character concept only), so wiring it here weakens no guard.
    onFile: (id, file) => void props.store.submitFile(id, file),
    onOpenGallery: props.onOpenGallery,
    onOpenSurface: props.onOpenSurface,
    assetUrl: props.assetUrl,
  };

  const stamp = (iso: string): string =>
    formatDateTime(iso, { timeZone: props.timeZone, locale: props.locale, part: 'time' });
  // The separator label AND the day-boundary key, from the same binding that
  // stamps the bubbles — so a separator can never claim a different day than the
  // timestamps under it.
  const day = (iso: string): string =>
    formatDateTime(iso, { timeZone: props.timeZone, locale: props.locale, part: 'date' });

  const companionAvatar = (): JSX.Element => (
    <Avatar
      alt={props.companion.name}
      name={props.companion.name}
      src={props.avatarSrc}
      size="sm"
    />
  );

  let scroller: HTMLDivElement | undefined;
  // Whether the reader is still at the live end of the thread. Sampled on scroll
  // rather than at anchor time, because by the time the effect runs the content
  // has already grown and the geometry no longer describes where the user was.
  const [following, setFollowing] = createSignal(true);
  const sampleFollowing = (): void => {
    if (scroller) {
      setFollowing(shouldFollow(scroller));
    }
  };

  // Follow the newest content as the run streams (items grow token by token) —
  // but ONLY while the reader has not scrolled away. Anchoring unconditionally
  // yanked a reader back to the bottom on every token, i.e. several times a
  // second during a reply, making the scrollback unusable exactly when there was
  // most to re-read.
  createEffect(
    on(
      () => [props.store.stream().items.length, props.store.history().length] as const,
      () => {
        queueMicrotask(() => {
          if (scroller && following()) {
            scroller.scrollTop = scroller.scrollHeight;
          }
        });
      },
    ),
  );

  const showLive = (): boolean => {
    const status = props.store.stream().status;
    return (
      status === 'streaming' || status === 'waiting_input' || props.store.stream().items.length > 0
    );
  };
  const typing = (): boolean =>
    props.store.stream().status === 'streaming' && props.store.stream().items.length === 0;

  // A gate `402` (out of credits / free daily cap) becomes the paywall; any other
  // error stays a plain retry line. Server-authoritative — derived from the error
  // the edge returned, never from a client-side cap count.
  // Two arrivals, one paywall: a gate `402` refused BEFORE the stream opened is
  // an `ApiError` on the store; the same gate tripped DURING a run — the
  // expensive case, mid-generation — arrives as a `run-error` frame carrying only
  // a code. Both route through `paywall-model`, so the two can never drift.
  const paywall = () =>
    paywallFromError(props.store.error()) ?? paywallFromCode(props.store.stream().errorCode);
  // The turn's OWN outcome, never "is there an error anywhere": `error` is also set
  // by a failed history reload after a turn already succeeded, and by a failed form
  // ack mid-stream — offering a retry for either re-sends an answered turn (a
  // duplicate row and a second credit) or paints an alert over a healthy reply. A
  // refusal that never opened the stream marks the turn failed itself
  // (`markStreamFailed`), so this one signal covers both shapes. The gate `402` is
  // excluded because the paywall below is its affordance.
  const failed = () => props.store.stream().status === 'error' && paywall() === null;
  // A `409` is not a fault — her reply to the previous turn is still running — so it
  // gets copy that says so, and the same retry control (which now works, because by
  // the time the user reads it the run has usually settled).
  const busyThread = () => props.store.error()?.code === 'conversation_busy';

  return (
    <div
      class="f4-stream"
      ref={scroller}
      onScroll={sampleFollowing}
      aria-live="polite"
      aria-busy={props.store.busy()}
    >
      <Show
        when={
          props.store.history().length > 0 ||
          showLive() ||
          props.store.pendingUserText() ||
          props.store.queued().length > 0
        }
        fallback={
          <p class="f4-stream__empty">
            {t('surface.emptyThread', { companionName: props.companion.name })}
          </p>
        }
      >
        {/* The honest half of the bounded thread read. The edge answers
            `GET /conversations/:id` with the NEWEST page and reports the cap;
            without this line the oldest bubble on screen reads as the start of
            the conversation, which is the silent truncation the cap was written
            to avoid. Above the first bubble, where the missing turns would be. */}
        <Show when={props.store.historyTruncated()}>
          <p class="f4-stream__truncated">{t('surface.historyTruncated')}</p>
        </Show>

        <For each={withDaySeparators(props.store.history(), day)}>
          {(row) => (
            <Switch>
              <Match when={row.kind === 'day' ? row : undefined}>
                {(day) => (
                  // A plain paragraph, not `role="separator"`: the date is
                  // content a screen reader should read in sequence, and
                  // `separator` is a divider whose text is not announced.
                  <p class="f4-stream__day">{day().label}</p>
                )}
              </Match>
              <Match when={row.kind === 'message' ? row : undefined}>
                {(entry) => (
                  <HistoryBubble
                    message={entry().message}
                    avatar={companionAvatar()}
                    timestamp={stamp(entry().message.createdAt)}
                    handlers={handlers}
                  />
                )}
              </Match>
            </Switch>
          )}
        </For>

        {/* Turns typed with no connection. They are durably queued and replayed
            on reconnect — a message that cannot leave the device is shown as
            waiting, never silently discarded. */}
        <For each={props.store.queued()}>
          {(entry) => (
            <ChatBubble variant="sent" status="sending" statusLabel={t('surface.queuedMessage')}>
              <span class="f4-stream__text">{entry.text}</span>
            </ChatBubble>
          )}
        </For>

        {/* The optimistic turn. `sent` rather than `sending`: it is only ever set
            once the edge accepted the turn and opened the run — the still-in-
            flight and never-left-the-device cases are the queued bubbles above. */}
        <Show when={props.store.pendingUserText()}>
          {(text) => (
            <ChatBubble variant="sent" status="sent">
              <span class="f4-stream__text">{text()}</span>
            </ChatBubble>
          )}
        </Show>

        <Show when={showLive()}>
          <Show
            when={!typing()}
            fallback={
              <ChatBubble
                variant="received"
                avatar={companionAvatar()}
                status="sending"
                statusLabel={t('chat.composer.typing', { companionName: props.companion.name })}
              >
                <span class="f4-stream__text" />
              </ChatBubble>
            }
          >
            <ChatBubble variant="received" avatar={companionAvatar()}>
              <div class="f4-stream__items">
                <For each={props.store.stream().items}>
                  {(item) => <StreamItemView item={item} handlers={handlers} />}
                </For>
              </div>
            </ChatBubble>
          </Show>
        </Show>

        {/* A failed turn must be recoverable IN PLACE: the copy says "tap to
            retry", so there is an actual control that re-runs the last turn. */}
        <Show when={failed()}>
          <div class="f4-stream__error" role="alert">
            <p class="f4-stream__errorText">
              {busyThread() ? t('surface.conversationBusy') : t('surface.streamError')}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={props.store.busy()}
              onClick={() => void props.store.retry()}
            >
              {t('actions.retry')}
            </Button>
          </div>
        </Show>

        <Show when={paywall()}>
          {(prompt) => (
            <Paywall prompt={prompt()} onOpenBilling={() => props.onOpenSurface?.('billing')} />
          )}
        </Show>
      </Show>
    </div>
  );
}

interface HistoryBubbleProps {
  readonly message: MessageView;
  readonly avatar: JSX.Element;
  readonly timestamp: string;
  readonly handlers: StreamHandlers;
}

/** One persisted turn — a plain sent bubble for the user, item-rendered for the companion. */
function HistoryBubble(props: HistoryBubbleProps): JSX.Element {
  return (
    <Show
      when={props.message.role !== 'user'}
      fallback={
        <ChatBubble variant="sent" status="sent" timestamp={props.timestamp}>
          <span class="f4-stream__text">{messageText(props.message.content)}</span>
        </ChatBubble>
      }
    >
      <ChatBubble variant="received" avatar={props.avatar} timestamp={props.timestamp}>
        <div class="f4-stream__items">
          <For each={messageItems(props.message.content)}>
            {(item) => <StreamItemView item={item} handlers={props.handlers} />}
          </For>
        </div>
      </ChatBubble>
    </Show>
  );
}
